import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  LogOut, FileText, MessageSquare, Menu, Eye, Trash2, 
  ChevronDown, ChevronUp, Phone, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// HTTP Status codes
const HTTP_UNAUTHORIZED = 401;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('briefs');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [briefs, setBriefs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total_briefs: 0, new_briefs: 0, total_contacts: 0 });
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [expandedBrief, setExpandedBrief] = useState(null);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin/login');
  }, [navigate]);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` }
  }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [briefsRes, contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/briefs`, getAuthHeaders()),
        axios.get(`${API}/contacts`, getAuthHeaders()),
        axios.get(`${API}/stats`, getAuthHeaders())
      ]);
      setBriefs(briefsRes.data);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, handleLogout]);

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    const user = sessionStorage.getItem('admin_user');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    try {
      setAdmin(JSON.parse(user));
    } catch {
      setAdmin(null);
    }
    fetchData();
  }, [navigate, fetchData]);

  const updateBriefStatus = async (briefId, status) => {
    try {
      await axios.patch(`${API}/briefs/${briefId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Status atualizado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const deleteBrief = async (briefId) => {
    if (!window.confirm('Tens a certeza que queres eliminar este briefing?')) return;
    try {
      await axios.delete(`${API}/briefs/${briefId}`, getAuthHeaders());
      toast.success('Briefing eliminado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar briefing');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Tens a certeza que queres eliminar este contacto?')) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`, getAuthHeaders());
      toast.success('Contacto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar contacto');
    }
  };

  // Export briefs to CSV
  const exportToCSV = () => {
    if (briefs.length === 0) {
      toast.error('Sem leads para exportar');
      return;
    }

    const headers = [
      'Nome', 'Email', 'Telefone', 'País', 'Estado', 'Propósito', 
      'Áreas', 'Tipo Imóvel', 'Orçamento Min', 'Orçamento Max', 
      'Prazo', 'Data'
    ];

    const rows = briefs.map(b => [
      b.full_name || '',
      b.email || '',
      b.phone || '',
      b.country_of_residence || '',
      statusLabels[b.status] || b.status,
      b.purchase_purpose?.replace('_', ' ') || '',
      (b.preferred_areas || []).join('; '),
      b.property_type?.replace('_', ' ') || '',
      b.budget_min || '',
      b.budget_max || '',
      b.purchase_timeframe?.replace('_', ' ') || '',
      new Date(b.created_at).toLocaleDateString('pt-PT')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV exportado!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-700',
    contacted: 'bg-yellow-100 text-yellow-700',
    in_progress: 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-700'
  };

  const statusLabels = {
    new: 'Novo',
    contacted: 'Contactado',
    in_progress: 'Em Progresso',
    completed: 'Concluído',
    archived: 'Arquivado'
  };

  const navItems = [
    { id: 'briefs', label: 'Leads', icon: FileText, count: stats.total_briefs },
    { id: 'contacts', label: 'Mensagens', icon: MessageSquare, count: stats.total_contacts }
  ];

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Portugal Elite Estates" className="w-10 h-10" />
              <div>
                <span className="font-heading font-semibold text-foreground">Admin</span>
                <span className="block text-xs text-muted-foreground">Portugal Elite</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="p-4 border-b">
            <div className="bg-primary/10 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-1">Novos leads</p>
              <p className="text-2xl font-heading font-semibold text-primary">{stats.new_briefs}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    activeTab === item.id 
                      ? 'bg-primary text-white' 
                      : 'text-foreground hover:bg-secondary'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === item.id ? 'bg-white/20' : 'bg-primary/10 text-primary'
                  }`}>
                    {item.count}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-primary font-semibold">{admin?.name?.charAt(0) || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{admin?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{admin?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1 h-9"
                onClick={() => window.open('/', '_blank')}
              >
                <Eye size={16} className="mr-1" />
                Ver Site
              </Button>
              <Button 
                variant="outline" 
                className="h-9 px-3"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Menu size={24} />
            </button>
            <h1 className="font-heading text-xl font-semibold">
              {activeTab === 'briefs' ? 'Leads' : 'Mensagens'}
            </h1>
          </div>
          
          {/* Export Button */}
          {activeTab === 'briefs' && briefs.length > 0 && (
            <Button 
              onClick={exportToCSV}
              variant="outline"
              className="gap-2"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              Exportar CSV
            </Button>
          )}
        </header>

        {/* Content Area */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* Briefs Tab */}
              {activeTab === 'briefs' && (
                <div className="space-y-4" data-testid="briefs-list">
                  {briefs.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border">
                      <FileText size={48} className="mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Ainda não há leads.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {briefs.map((brief) => (
                        <div key={brief.id} className="bg-white rounded-xl border overflow-hidden">
                          {/* Brief Header */}
                          <div 
                            className="p-5 flex items-center justify-between cursor-pointer hover:bg-secondary/20"
                            onClick={() => setExpandedBrief(expandedBrief === brief.id ? null : brief.id)}
                          >
                            <div className="flex items-center gap-4">
                              <div>
                                <h3 className="font-medium text-foreground">{brief.full_name}</h3>
                                <p className="text-sm text-muted-foreground">{brief.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`text-xs px-3 py-1 rounded-full ${statusColors[brief.status]}`}>
                                {statusLabels[brief.status]}
                              </span>
                              <span className="text-xs text-muted-foreground hidden sm:block">
                                {formatDate(brief.created_at)}
                              </span>
                              {expandedBrief === brief.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                          </div>

                          {/* Brief Details */}
                          {expandedBrief === brief.id && (
                            <div className="border-t p-5 bg-secondary/10">
                              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Propósito</p>
                                  <p className="text-foreground capitalize">{brief.purchase_purpose?.replace('_', ' ')}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Áreas</p>
                                  <p className="text-foreground capitalize">{brief.preferred_areas?.join(', ').replace(/_/g, ' ')}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Tipo Imóvel</p>
                                  <p className="text-foreground capitalize">{brief.property_type?.replace('_', ' ')}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Orçamento</p>
                                  <p className="text-foreground">
                                    {(() => {
                                      const hasBoth = brief.budget_min && brief.budget_max;
                                      const hasOnlyMax = !brief.budget_min && brief.budget_max;
                                      
                                      if (hasBoth) {
                                        return `€${parseInt(brief.budget_min).toLocaleString()} - €${parseInt(brief.budget_max).toLocaleString()}`;
                                      }
                                      if (hasOnlyMax) {
                                        return `Até €${parseInt(brief.budget_max).toLocaleString()}`;
                                      }
                                      return 'Não especificado';
                                    })()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Prazo</p>
                                  <p className="text-foreground capitalize">{brief.purchase_timeframe?.replace('_', ' ') || 'Não especificado'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">País</p>
                                  <p className="text-foreground">{brief.country_of_residence || 'Não especificado'}</p>
                                </div>
                              </div>

                              {brief.property_description && (
                                <div className="mb-6">
                                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Descrição</p>
                                  <p className="text-foreground">{brief.property_description}</p>
                                </div>
                              )}

                              {brief.phone && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                                  <Phone size={14} />
                                  <a href={`tel:${brief.phone}`} className="hover:text-primary">{brief.phone}</a>
                                </div>
                              )}

                              <div className="flex items-center gap-4 pt-4 border-t">
                                <Select 
                                  value={brief.status} 
                                  onValueChange={(v) => updateBriefStatus(brief.id, v)}
                                >
                                  <SelectTrigger className="w-40">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new">Novo</SelectItem>
                                    <SelectItem value="contacted">Contactado</SelectItem>
                                    <SelectItem value="in_progress">Em Progresso</SelectItem>
                                    <SelectItem value="completed">Concluído</SelectItem>
                                    <SelectItem value="archived">Arquivado</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteBrief(brief.id)}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={16} className="mr-1" />
                                  Eliminar
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="space-y-4" data-testid="contacts-list">
                  {contacts.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border">
                      <MessageSquare size={48} className="mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Ainda não há mensagens.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contacts.map((contact) => (
                        <div key={contact.id} className="bg-white rounded-xl border p-5">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-foreground">{contact.name}</h3>
                              <p className="text-sm text-muted-foreground">{contact.email}</p>
                              {contact.phone && (
                                <p className="text-sm text-muted-foreground mt-1">{contact.phone}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {formatDate(contact.created_at)}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteContact(contact.id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                          {contact.message && (
                            <p className="mt-4 text-foreground bg-secondary/30 p-4 rounded-lg">
                              {contact.message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
